import { Injectable } from '@angular/core';
import { select } from '@angular-redux/store';
import { find, forEach, get, isEmpty, set } from 'lodash';

import { Observable, observableCombineLatest, rx } from '@app/shared/rx';
import { IBaseEntityType, ICompany } from '@app/shared/types';
import {
  convertDateToUTCDate,
  convertUTCDateToTimeZone,
  getCurrentTimeByTimeZone,
} from '@app/shared/utils/date-utils';
import { TIME_FORMATS } from '@config/index';

@Injectable()
export class CompanyUtilService {
  @select(['userSession', 'companies']) public companies$: Observable<
    ICompany[]
  >;
  @select(['userSession', 'currentCompanyId'])
  public currentCompanyId$: Observable<string>;

  public currentCompany: ICompany;

  public async initCurrentCompany(): Promise<void> {
    this.currentCompany = await this.getCurrentCompanyDataObs()
      .pipe(rx.take(1))
      .toPromise();
  }

  public constructor() {
    this.initCurrentCompany();
  }

  public getCurrentCompanyDataObs(): Observable<ICompany> {
    return observableCombineLatest([
      this.companies$,
      this.currentCompanyId$,
    ]).pipe(
      rx.filter(
        ([companies, currentCompanyId]) =>
          !isEmpty(companies) && currentCompanyId?.length > 0,
      ),
      rx.distinctUntilChanged(),
      rx.map(([companies, currentCompanyId]) => {
        return find(companies, (company) => company.id === currentCompanyId);
      }),
    );
  }

  public getCurrentCompanyTimeZoneObs(): Observable<string> {
    return this.getCurrentCompanyDataObs().pipe(
      rx.map((company) => company.timezone),
    );
  }

  public covertDateBetweenUTCAndCurrentTimeZone<
    T extends IBaseEntityType = IBaseEntityType,
  >(
    doc: T,
    convertDateFields: string[],
    isUTCToCurrentTimeZone: boolean = false,
    format: TIME_FORMATS = TIME_FORMATS.L,
  ): T {
    if (isEmpty(convertDateFields) || isEmpty(doc)) return doc;

    forEach(convertDateFields, (dateField) => {
      const fieldDate = get(doc, dateField, null);

      if (isEmpty(fieldDate)) return;

      const formattedFieldDate = this.convertUTCDateWithTimeZone(
        fieldDate,
        isUTCToCurrentTimeZone,
        format,
      );
      set(doc, dateField, formattedFieldDate);
    });

    return doc;
  }

  public convertUTCDateWithTimeZone(
    date: string,
    isUTCToCurrentTimeZone: boolean = false,
    format: TIME_FORMATS = TIME_FORMATS.L,
  ): string {
    return isUTCToCurrentTimeZone
      ? convertUTCDateToTimeZone(date, this.currentCompany?.timezone, format)
      : convertDateToUTCDate(date, this.currentCompany?.timezone);
  }

  public getCurrentTime(format: TIME_FORMATS = TIME_FORMATS.L): string {
    return getCurrentTimeByTimeZone(this.currentCompany?.timezone, format);
  }
}
